import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, Clock, Paperclip, X, Plus, Link } from 'lucide-react';

const RequestsInbox = ({ onSubmit, onNext, onPrevious, initialData = {} }) => {
  const { t } = useTranslation();
  const [requests, setRequests] = useState(initialData.requests || [
    {
      id: 'REQ-001',
      type: 'General',
      standard: 'E1',
      severity: 'Minor',
      assignee: 'John Smith',
      due: '2024-02-15',
      status: 'Open',
      age: 2,
      prompt: 'Please provide clarification on the methodology used for Scope 1 emissions calculation.',
      owner: 'Auditor Team',
      slaTimer: '48h remaining'
    },
    {
      id: 'REQ-002',
      type: 'Sample',
      standard: 'S1',
      severity: 'Major',
      assignee: 'Sarah Johnson',
      due: '2024-02-10',
      status: 'Waiting',
      age: 5,
      prompt: 'Request for sample documentation for energy consumption data.',
      owner: 'Auditor Team',
      slaTimer: 'Overdue'
    }
  ]);

  const [selectedRequest, setSelectedRequest] = useState(null);
  const [filters, setFilters] = useState({
    type: '',
    standard: '',
    severity: '',
    assignee: '',
    status: '',
    overdue: false
  });

  const [replyText, setReplyText] = useState('');

  const requestTypes = ['General', 'Sample', 'Policy', 'Calc'];
  const standards = ['E1', 'E2', 'E3', 'E4', 'E5', 'S1', 'S2', 'S3', 'S4', 'G1'];
  const severities = ['Info', 'Minor', 'Major', 'Critical'];
  const statuses = ['Open', 'Waiting', 'Closed'];

  const filteredRequests = requests.filter(request => {
    if (filters.type && request.type !== filters.type) return false;
    if (filters.standard && request.standard !== filters.standard) return false;
    if (filters.severity && request.severity !== filters.severity) return false;
    if (filters.assignee && request.assignee !== filters.assignee) return false;
    if (filters.status && request.status !== filters.status) return false;
    if (filters.overdue && request.status !== 'Overdue') return false;
    return true;
  });

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleRequestClick = (request) => {
    setSelectedRequest(request);
  };

  const handleReply = () => {
    if (!selectedRequest || !replyText.trim()) return;

    const updatedRequests = requests.map(req => {
      if (req.id === selectedRequest.id) {
        return {
          ...req,
          status: 'Closed',
          reply: replyText,
          repliedAt: new Date().toISOString()
        };
      }
      return req;
    });

    setRequests(updatedRequests);
    setReplyText('');
    setSelectedRequest(null);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Open': return 'bg-blue-100 text-blue-800';
      case 'Waiting': return 'bg-yellow-100 text-yellow-800';
      case 'Closed': return 'bg-green-100 text-green-800';
      case 'Overdue': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'Info': return 'bg-gray-100 text-gray-800';
      case 'Minor': return 'bg-blue-100 text-blue-800';
      case 'Major': return 'bg-yellow-100 text-yellow-800';
      case 'Critical': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">
          {t('workflow.auditQA.requestsInbox.title')}
        </h2>
        
        <p className="text-gray-600 mb-8">
          {t('workflow.auditQA.requestsInbox.description')}
        </p>

        {/* Status Summary */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <span className="text-sm font-medium text-blue-800">
                {t('workflow.auditQA.requestsInbox.requests')}: {requests.filter(r => r.status === 'Open').length} {t('workflow.auditQA.requestsInbox.open')}
              </span>
              <span className="text-sm text-blue-700">
                {t('workflow.auditQA.requestsInbox.overdue')}: {requests.filter(r => r.status === 'Overdue').length}
              </span>
              <span className="text-sm text-blue-700">
                {t('workflow.auditQA.requestsInbox.materialChanges')}: —
              </span>
              <span className="text-sm text-blue-700">
                {t('workflow.auditQA.requestsInbox.opinion')}: —
              </span>
            </div>
          </div>
        </div>

      <div className={`grid grid-cols-1 gap-6 ${selectedRequest ? 'lg:grid-cols-3' : 'lg:grid-cols-1'}`}>
        {/* Filters and Requests List */}
        <div className={selectedRequest ? 'lg:col-span-2' : 'lg:col-span-1'}>
          {/* Filters */}
          <div className="bg-gray-50 rounded-lg p-4 mb-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800">Filters</h3>
              <button className="text-blue-600 hover:text-blue-800 text-sm">
                Clear All
              </button>
            </div>
            
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                <select
                  value={filters.type}
                  onChange={(e) => handleFilterChange('type', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Types</option>
                  {requestTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Standard</label>
                <select
                  value={filters.standard}
                  onChange={(e) => handleFilterChange('standard', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Standards</option>
                  {standards.map(standard => (
                    <option key={standard} value={standard}>{standard}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Severity</label>
                <select
                  value={filters.severity}
                  onChange={(e) => handleFilterChange('severity', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Severities</option>
                  {severities.map(severity => (
                    <option key={severity} value={severity}>{severity}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Statuses</option>
                  {statuses.map(status => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </select>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="overdue"
                  checked={filters.overdue}
                  onChange={(e) => handleFilterChange('overdue', e.target.checked)}
                  className="mr-2"
                />
                <label htmlFor="overdue" className="text-sm font-medium text-gray-700">
                  Overdue Only
                </label>
              </div>
            </div>
          </div>

          {/* Requests Table */}
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-800">
                {t('workflow.auditQA.requestsInbox.requests')} ({filteredRequests.length})
              </h3>
              <div className="flex space-x-2">
                <button className="text-blue-600 hover:text-blue-800 text-sm flex items-center">
                  <Plus className="w-4 h-4 mr-1" />
                  {t('workflow.auditQA.requestsInbox.newRequest')}
                </button>
                <button className="text-blue-600 hover:text-blue-800 text-sm flex items-center">
                  <Download className="w-4 h-4 mr-1" />
                  {t('workflow.auditQA.requestsInbox.exportCSV')}
                </button>
              </div>
            </div>
            
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('workflow.auditQA.requestsInbox.id')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('workflow.auditQA.requestsInbox.type')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('workflow.auditQA.requestsInbox.standard')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('workflow.auditQA.requestsInbox.severity')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('workflow.auditQA.requestsInbox.assignee')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('workflow.auditQA.requestsInbox.due')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('workflow.auditQA.requestsInbox.status')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredRequests.map((request) => (
                  <tr 
                    key={request.id}
                    onClick={() => handleRequestClick(request)}
                    className={`cursor-pointer hover:bg-gray-50 ${
                      selectedRequest?.id === request.id ? 'bg-blue-50' : ''
                    }`}
                  >
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                      {request.id}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                      {request.type}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                      {request.standard}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getSeverityColor(request.severity)}`}>
                        {request.severity}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                      {request.assignee}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                      {request.due}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(request.status)}`}>
                        {request.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Request Details */}
        {selectedRequest && (
          <div className="lg:col-span-1">
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-800">
                  {selectedRequest.id}
                </h3>
                <button
                  onClick={() => setSelectedRequest(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <h4 className="font-medium text-gray-800 mb-2">Request Details</h4>
                  <div className="bg-white rounded-lg p-3">
                    <p className="text-sm text-gray-700 mb-3">{selectedRequest.prompt}</p>
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <span>Owner: {selectedRequest.owner}</span>
                      <span className="flex items-center">
                        <Clock className="w-3 h-3 mr-1" />
                        {selectedRequest.slaTimer}
                      </span>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="font-medium text-gray-800 mb-2">Reply</h4>
                  <textarea
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    placeholder="Type your reply here..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={4}
                  />
                  
                  <div className="flex items-center justify-between mt-2">
                    <div className="flex space-x-2">
                      <button className="text-blue-600 hover:text-blue-800 text-sm flex items-center">
                        <Paperclip className="w-4 h-4 mr-1" />
                        {t('workflow.auditQA.requestsInbox.attachEvidence')}
                      </button>
                      <button className="text-blue-600 hover:text-blue-800 text-sm flex items-center">
                        <Link className="w-4 h-4 mr-1" />
                        {t('workflow.auditQA.requestsInbox.linkSource')}
                      </button>
                    </div>
                    <button
                      onClick={handleReply}
                      disabled={!replyText.trim()}
                      className="bg-blue-500 text-white py-1 px-3 rounded text-sm hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {t('workflow.auditQA.requestsInbox.sendReply')}
                    </button>
                  </div>
                </div>

                <div>
                  <h4 className="font-medium text-gray-800 mb-2">{t('workflow.auditQA.requestsInbox.actions')}</h4>
                  <div className="space-y-2">
                    <button className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-white rounded">
                      {t('workflow.auditQA.requestsInbox.close')}
                    </button>
                    <button className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-white rounded">
                      {t('workflow.auditQA.requestsInbox.askClarification')}
                    </button>
                    <button className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-white rounded">
                      {t('workflow.auditQA.requestsInbox.escalate')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

        {/* Action Buttons */}
        <div className="flex justify-end pt-6 border-t border-gray-200">
          <button
            type="button"
            className="px-6 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
          >
            {t('workflow.auditQA.requestsInbox.markAllResolved')}
          </button>
        </div>
    </div>
  );
};

export default RequestsInbox;
